import { useCallback, useState } from 'react';
import { logInfo } from '@betty/toolkit/logging';
import {
  BottomSheet,
  useNavigation,
  StackNavigationProp,
} from '@betty/navigation';
import { DepositNavigatorParamsList } from '../navigators/DepositNavigator/DepositNavigator.types';
import { DepositPromos } from '../components/Deposit/Promos/Promos.types';
import { DepositPromoId } from '../state/deposit/deposit.types';
import {
  findNonWelcomePromoId,
  findOptedInPromoId,
} from '../components/Deposit/Deposit.utils';
import PromosService from '../services/PromosService/PromosService';

const useDepositPromoHandler = (
  promos?: DepositPromos,
  selectedAmount: number = 0,
  selectedPromoIds?: DepositPromoId[],
) => {
  const navigation =
    useNavigation<StackNavigationProp<DepositNavigatorParamsList>>();
  const [loading, setLoading] = useState(false);

  const setDepositPromo = useCallback(async () => {
    if (!selectedAmount || !selectedPromoIds) {
      return;
    }

    const nonWelcomePromoId = findNonWelcomePromoId(selectedPromoIds, promos);
    const optedInPromoId = findOptedInPromoId(promos);

    try {
      if (nonWelcomePromoId !== undefined) {
        setLoading(true);

        await PromosService.optIn({
          pathParams: { promoId: nonWelcomePromoId },
        });

        logInfo('Deposit', `Deposit launch for promo ${nonWelcomePromoId}`);
      }

      if (!selectedPromoIds.length && optedInPromoId !== undefined) {
        setLoading(true);

        await PromosService.optOut({
          pathParams: { promoId: optedInPromoId },
        });
      }

      navigation.navigate('DepositPaymentsScreen', { amount: selectedAmount });
    } catch (error) {
      logInfo('Deposit', 'Promo error', error);
    } finally {
      setLoading(false);
    }
  }, [promos, selectedAmount, selectedPromoIds, navigation]);

  const handleDepositPromo = useCallback(() => {
    const showNoPromoConfirmation =
      !selectedPromoIds?.length &&
      promos?.ids.some((promoId) => {
        const { subTypeMeta = {} } = promos.byId[promoId];
        const { minimumQualificationAmount } = subTypeMeta;

        return (
          minimumQualificationAmount !== undefined &&
          minimumQualificationAmount <= selectedAmount
        );
      });

    if (showNoPromoConfirmation) {
      BottomSheet.open({
        namespace: 'wallet',
        type: 'DepositNoPromo',
        data: {
          onConfirm: setDepositPromo,
        },
      });

      return;
    }

    setDepositPromo();
  }, [promos, selectedAmount, selectedPromoIds, setDepositPromo]);

  return { loading, handleDepositPromo };
};

export default useDepositPromoHandler;
